import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Category } from "Types/Navbar";
import { encodeParam } from "Utilities/urlEncoder";
import { useSearchAnalytics } from "Context/SearchAnalyticsContext";

interface AuthorsDropdownProps {
  style: React.CSSProperties;
  categories: Category[];
  authors: string[];
  hoveredCategory: string | null;
  onHoverCategory: (category: string | null) => void;
  onMouseLeave: () => void;
}

const ITEM_HEIGHT = 36;
const MAX_VISIBLE_ITEMS = 10;

const AuthorsDropdown: React.FC<AuthorsDropdownProps> = ({
  style,
  categories,
  authors,
  hoveredCategory,
  onHoverCategory,
  onMouseLeave,
}) => {
  const navigate = useNavigate();
  const { queueAnalytics, resolveAnalytics } = useSearchAnalytics();
  const searchTimeoutsRef = useRef<Record<string, NodeJS.Timeout>>({});
  const lastLoggedRef = useRef<Record<string, string>>({});

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      Object.values(searchTimeoutsRef.current).forEach(clearTimeout);
    };
  }, []);

  const [categorySearch, setCategorySearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const filteredCategories = useMemo(() => {
    return categories.filter((category) =>
      category.name
        .toLowerCase()
        .includes(categorySearch.toLowerCase()),
    );
  }, [categories, categorySearch]);

  const handleCategoryClick = (categoryCode: string) => {
    const categoryName = categories.find((c) => c.code === categoryCode)?.name ?? categoryCode;
    queueAnalytics("CATEGORY", categoryName);
    navigate(`/books-listing?category=${encodeParam(categoryCode)}`);
  };

  const handleAuthorClick = (categoryCode: string, author: string) => {
    queueAnalytics("AUTHOR", author);
    navigate(
      `/books-listing?category=${encodeParam(categoryCode)}&author=${encodeParam(author)}`,
    );
  };

  return (
    <>
      <div
        className="absolute top-full mt-0 w-[50vw] xl:w-[25vw] bg-vintageBg bg-opacity-90 rounded-2xl z-50 grid grid-cols-2 shadow-lg"
        style={style}
        onMouseLeave={onMouseLeave}
      >
        <div className="w-full p-4">
          <>
            <div className="relative w-full mb-3">
              <input
                type="text"
                placeholder="Search categories..."
                value={categorySearch}
                onChange={(e) => {
                  const val = e.target.value;
                  setCategorySearch(val);
                  const key = "category";
                  if (searchTimeoutsRef.current[key]) {
                    clearTimeout(searchTimeoutsRef.current[key]);
                  }
                  if (!val.trim()) {
                    lastLoggedRef.current[key] = "";
                  } else {
                    searchTimeoutsRef.current[key] = setTimeout(() => {
                      const matched = categories.filter((category) =>
                        category.name.toLowerCase().includes(val.toLowerCase())
                      );
                      if (matched.length === 0) {
                        const trimmed = val.trim();
                        const lastLogged = lastLoggedRef.current[key] || "";
                        const searchType = "CATEGORY";
                        if (lastLogged && trimmed.startsWith(lastLogged)) {
                          try {
                            const logsKey = "sa_no_result_logs";
                            const logs = JSON.parse(localStorage.getItem(logsKey) || "[]");
                            const lastIndex = [...logs].reverse().findIndex(
                              (log: any) => log.searchType === searchType && log.searchData === lastLogged
                            );
                            if (lastIndex !== -1) {
                              const targetIndex = logs.length - 1 - lastIndex;
                              logs.splice(targetIndex, 1);
                              localStorage.setItem(logsKey, JSON.stringify(logs));
                            }
                          } catch (err) {
                            console.error("Error updating logs:", err);
                          }
                        } else if (lastLogged && lastLogged.startsWith(trimmed)) {
                          return;
                        }
                        queueAnalytics(searchType, trimmed);
                        resolveAnalytics(false);
                        lastLoggedRef.current[key] = trimmed;
                      }
                    }, 800);
                  }
                }}
                className="w-full pr-8 px-2 py-1 text-sm border rounded-md focus:outline-none"
              />
              <div
                className="absolute right-2 top-0 h-full flex items-center text-gray-700 text-opacity-50 cursor-pointer hover:text-vintageText transition-colors"
                tabIndex={0}
                role="button"
                aria-label="Search Categories"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-search"
                >
                  <path d="m21 21-4.34-4.34" />
                  <circle cx="11" cy="11" r="8" />
                </svg>
              </div>
            </div>

            {filteredCategories.length === 0 ? (
              <p className="text-sm text-gray-500">No results found</p>
            ) : (
              <ul
                className="space-y-2 overflow-y-auto hide-scrollbar"
                style={{ maxHeight: `${ITEM_HEIGHT * MAX_VISIBLE_ITEMS}px` }}
              >
                {filteredCategories.map((category, index) => (
                  <CategoryItem
                    key={index}
                    category={category}
                    onHover={onHoverCategory}
                    onClick={handleCategoryClick}
                  />
                ))}
              </ul>
            )}
          </>
        </div>

        <div className="w-full p-4">
          <AuthorList
            categories={categories}
            authors={authors}
            hoveredCategory={hoveredCategory}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
            onAuthorClick={handleAuthorClick}
            onClose={onMouseLeave}
          />
        </div>
      </div>

      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
    </>
  );
};

const CategoryItem: React.FC<{
  category: Category;
  onHover: (category: string | null) => void;
  onClick: (categoryCode: string) => void;
}> = ({ category, onHover, onClick }) => (
  <li
    className="py-2 px-2 cursor-pointer text-sm lg:text-base text-gray-800 font-gilroyRegular font-semibold tracking-wider flex justify-between items-center hover:text-vintageText"
    onMouseEnter={() => onHover(category.name)}
    onClick={() => onClick(category.code)}
  >
    <span className="flex-1 truncate mr-2" title={category.name}>
      {category.name}
    </span>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  </li>
);

const AuthorList: React.FC<{
  categories: Category[];
  authors: string[];
  hoveredCategory: string | null;
  activeIndex: number;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
  onAuthorClick: (categoryCode: string, author: string) => void;
  onClose: () => void;
}> = ({
  categories,
  authors,
  hoveredCategory,
  activeIndex,
  setActiveIndex,
  onAuthorClick,
  onClose,
}) => {
  const { queueAnalytics, resolveAnalytics } = useSearchAnalytics();
  const searchTimeoutsRef = useRef<Record<string, NodeJS.Timeout>>({});
  const lastLoggedRef = useRef<Record<string, string>>({});

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      Object.values(searchTimeoutsRef.current).forEach(clearTimeout);
    };
  }, []);

  const [inputValue, setInputValue] = useState("");

  const hoveredCategoryCode = useMemo(
    () => categories.find((c) => c.name === hoveredCategory)?.code ?? hoveredCategory,
    [categories, hoveredCategory],
  );

  const filteredAuthors = useMemo(() => {
    return authors.filter((author) =>
      author.toLowerCase().includes(inputValue.toLowerCase()),
    );
  }, [authors, inputValue]);

  useEffect(() => {
    setInputValue("");
  }, [hoveredCategory]);

  if (!hoveredCategory) {
    return (
      <p className="text-sm text-gray-500">
        Hover over a category to see authors
      </p>
    );
  }

  if (authors.length === 0) {
    return (
      <p className="text-sm text-gray-500">No authors for this category</p>
    );
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!filteredAuthors.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev < filteredAuthors.length - 1 ? prev + 1 : prev,
      );
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : 0));
    }

    if (e.key === "Enter") {
      onAuthorClick(hoveredCategoryCode!, filteredAuthors[activeIndex]);
    }

    if (e.key === "Escape") {
      onClose();
    }
  };

  return (
    <>
      <div className="relative w-full mb-3">
        <input
          type="text"
          placeholder="Search authors..."
          value={inputValue}
          onChange={(e) => {
            const val = e.target.value;
            setInputValue(val);
            const key = "author";
            if (searchTimeoutsRef.current[key]) {
              clearTimeout(searchTimeoutsRef.current[key]);
            }
            if (!val.trim()) {
              lastLoggedRef.current[key] = "";
            } else {
              searchTimeoutsRef.current[key] = setTimeout(() => {
                const matched = authors.filter((author) =>
                  author.toLowerCase().includes(val.toLowerCase())
                );
                if (matched.length === 0) {
                  const trimmed = val.trim();
                  const lastLogged = lastLoggedRef.current[key] || "";
                  const searchType = "AUTHOR";
                  if (lastLogged && trimmed.startsWith(lastLogged)) {
                    try {
                      const logsKey = "sa_no_result_logs";
                      const logs = JSON.parse(localStorage.getItem(logsKey) || "[]");
                      const lastIndex = [...logs].reverse().findIndex(
                        (log: any) => log.searchType === searchType && log.searchData === lastLogged
                      );
                      if (lastIndex !== -1) {
                        const targetIndex = logs.length - 1 - lastIndex;
                        logs.splice(targetIndex, 1);
                        localStorage.setItem(logsKey, JSON.stringify(logs));
                      }
                    } catch (err) {
                      console.error("Error updating logs:", err);
                    }
                  } else if (lastLogged && lastLogged.startsWith(trimmed)) {
                    return;
                  }
                  queueAnalytics(searchType, trimmed);
                  resolveAnalytics(false);
                  lastLoggedRef.current[key] = trimmed;
                }
              }, 800);
            }
          }}
          onKeyDown={handleKeyDown}
          className="w-full pr-8 px-2 py-1 text-sm border rounded-md focus:outline-none"
        />
        <div
          className="absolute right-2 top-0 h-full flex items-center text-gray-700 text-opacity-50 cursor-pointer hover:text-vintageText transition-colors"
          tabIndex={0}
          role="button"
          aria-label="Search Authors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-search"
          >
            <path d="m21 21-4.34-4.34" />
            <circle cx="11" cy="11" r="8" />
          </svg>
        </div>
      </div>

      {filteredAuthors.length === 0 ? (
        <p className="text-sm text-gray-500">No results found</p>
      ) : (
        <ul
          className="space-y-2 overflow-y-auto hide-scrollbar"
          style={{ maxHeight: `${ITEM_HEIGHT * MAX_VISIBLE_ITEMS}px` }}
        >
          {filteredAuthors.map((author, index) => (
            <li
              key={index}
              onClick={() => onAuthorClick(hoveredCategoryCode!, author)}
              className={`py-1 px-2 cursor-pointer text-sm lg:text-base font-gilroyRegular font-semibold tracking-wider rounded-md ${
                index === activeIndex
                  ? "bg-vintageText bg-opacity-10"
                  : "hover:bg-vintageText hover:bg-opacity-5"
              }`}
            >
              <span className="truncate block" title={author}>
                {author}
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

export default AuthorsDropdown;
